import {
  __decorateClass,
  init_buffer,
  init_define_environment,
  init_dirname,
  init_process
} from "./chunk-OKSKVPOI.js";

// libs/sdk/src/lib/api/public-api.ts
init_define_environment();
init_dirname();
init_buffer();
init_process();

// libs/sdk/src/lib/api/one-inch-dev-portal.adapter.ts
init_define_environment();
init_dirname();
init_buffer();
init_process();
import { CacheActivePromise } from "@one-inch-community/core/decorators";
import { TimeCache } from "@one-inch-community/core/cache";
import { getEnvironmentValue as getEnvironmentValue2 } from "@one-inch-community/core/environment";

// libs/sdk/src/lib/api/fusion-sdk.ts
init_define_environment();
init_dirname();
init_buffer();
init_process();
import { getEnvironmentValue } from "@one-inch-community/core/environment";
import { getClient } from "@one-inch-community/sdk/chain";
async function buildFusionSdk(chainId, walletController) {
  const { FusionSDK } = await import("./esm-MKEHQMEY.js");
  return new FusionSDK({
    url: getEnvironmentValue("oneInchDevPortalHost") + "/fusion",
    authKey: getEnvironmentValue("oneInchDevPortalToken"),
    network: chainId,
    blockchainProvider: new BlockchainFusionProviderConnector(chainId, walletController),
    httpProvider: new FusionHttpProviderConnector()
  });
}
var BlockchainFusionProviderConnector = class {
  constructor(chainId, walletController) {
    this.chainId = chainId;
    this.walletController = walletController;
    this.client = getClient(this.chainId);
  }
  async signTypedData(_, typedData) {
    return await this.walletController.signTypedData(typedData);
  }
  async ethCall(contractAddress, callData) {
    const resp = await this.client.call({
      to: contractAddress,
      data: callData
    });
    return resp.data ?? "";
  }
};
var FusionHttpProviderConnector = class {
  async get(url) {
    const resp = await fetch(url);
    return resp.json();
  }
  async post(url, data) {
    const resp = await fetch(url, {
      method: "POST",
      body: JSON.stringify(data)
    });
    return resp.json().catch(() => void 0);
  }
};

// libs/sdk/src/lib/api/one-inch-dev-portal.adapter.ts
import { cancelFusionOrder } from "@one-inch-community/sdk/chain";
var tokenPriceCache = new TimeCache(1e4);
var fusionQuoteReceiveCache = new TimeCache(5e3);
var gasPriceCache = new TimeCache(5e3);
var OneInchDevPortalAdapter = class {
  constructor() {
    this.host = getEnvironmentValue2("oneInchDevPortalHost");
    this.apiToken = getEnvironmentValue2("oneInchDevPortalToken");
  }
  async init(context) {
    this.context = context;
  }
  async getWhiteListedTokens(chainId) {
    const response = await fetch(`${this.host}/token/v1.2/${chainId}/token-list`, { headers: this.makeHeaders() });
    const data = await response.json();
    return data.tokens ?? [];
  }
  async getBalancesByWalletAddress(chainId, walletAddress) {
    const response = await fetch(`${this.host}/balance/v1.2/${chainId}/balances/${walletAddress}`, { headers: this.makeHeaders() });
    return await response.json();
  }
  async getTokenPrices(chainId) {
    const cacheValue = tokenPriceCache.get(chainId);
    if (cacheValue) {
      return cacheValue;
    }
    const queryParams = new URLSearchParams({ currency: "USD" });
    const response = await fetch(`${this.host}/price/v1.1/${chainId}?${queryParams}`, { headers: this.makeHeaders() });
    const result = await response.json();
    tokenPriceCache.set(chainId, result);
    return result;
  }
  async getFusionQuoteReceive(chainId, fromTokenAddress, toTokenAddress, amount, walletAddress, customPreset, enableEstimate = false) {
    const { auctionDuration, auctionEndAmount, auctionStartAmount, points } = customPreset ?? {};
    const id = [
      chainId,
      fromTokenAddress,
      toTokenAddress,
      amount,
      walletAddress,
      enableEstimate,
      auctionDuration,
      auctionEndAmount,
      auctionStartAmount,
      points?.join(",")
    ].filter(Boolean).join(":");
    const cacheData = fusionQuoteReceiveCache.get(id);
    if (cacheData) {
      return cacheData;
    }
    const queryParams = new URLSearchParams({
      fromTokenAddress,
      toTokenAddress,
      walletAddress,
      amount: amount.toString(),
      enableEstimate: enableEstimate.toString(),
      isLedgerLive: "false"
    });
    const response = await fetch(`${this.host}/fusion/quoter/v2.0/${chainId}/quote/receive?${queryParams}`, {
      method: !customPreset ? "GET" : "POST",
      body: customPreset && JSON.stringify(customPreset),
      headers: this.makeHeaders()
    });
    if (!response.ok) {
      fusionQuoteReceiveCache.set(id, null);
      return null;
    }
    const result = await response.json();
    fusionQuoteReceiveCache.set(id, result);
    return { ...result, chainId };
  }
  async getGasPrice(chainId) {
    const cacheValue = gasPriceCache.get(chainId);
    if (cacheValue) {
      return cacheValue;
    }
    const response = await fetch(`${this.host}/gas-price/v1.5/${chainId}`, { headers: this.makeHeaders() });
    if (!response.ok) {
      return null;
    }
    const result = await response.json();
    gasPriceCache.set(chainId, result);
    return result;
  }
  async getFusionOrderStatus(chainId, orderHash) {
    const sdk = await buildFusionSdk(chainId, this.context.connectWalletController);
    return await sdk.getOrderStatus(orderHash);
  }
  async cancelFusionOrder(chainId, orderHash) {
    const status = await this.getFusionOrderStatus(chainId, orderHash);
    const wallet = await this.context.connectWalletController.data.getActiveAddress();
    if (!wallet) throw new Error("");
    const result = await cancelFusionOrder(chainId, wallet, status.order.makerTraits, orderHash);
    return this.context.connectWalletController.writeContract(result.request);
  }
  makeHeaders() {
    const headers = new Headers();
    headers.set("Accept", "application/json");
    headers.set("Content-Type", "application/json");
    this.apiToken && headers.set("Authorization", this.apiToken);
    return headers;
  }
};
__decorateClass([
  CacheActivePromise()
], OneInchDevPortalAdapter.prototype, "getWhiteListedTokens", 1);
__decorateClass([
  CacheActivePromise()
], OneInchDevPortalAdapter.prototype, "getBalancesByWalletAddress", 1);
__decorateClass([
  CacheActivePromise()
], OneInchDevPortalAdapter.prototype, "getTokenPrices", 1);
__decorateClass([
  CacheActivePromise()
], OneInchDevPortalAdapter.prototype, "getFusionQuoteReceive", 1);
__decorateClass([
  CacheActivePromise()
], OneInchDevPortalAdapter.prototype, "getGasPrice", 1);
__decorateClass([
  CacheActivePromise()
], OneInchDevPortalAdapter.prototype, "getFusionOrderStatus", 1);
__decorateClass([
  CacheActivePromise()
], OneInchDevPortalAdapter.prototype, "cancelFusionOrder", 1);
export {
  OneInchDevPortalAdapter,
  buildFusionSdk
};
//# sourceMappingURL=index.esm.js.map
